import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createProduct } from '@/api/business/product'
import { ProductsKeys } from '@/queries/useProductQuery'
import Button from '@/components/Button'
import { Input } from '@/components/ui/input' 
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage, 
} from '@/components/ui/form' 
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'  
import { useEffect, useMemo } from 'react'
import { MarkProductInMarketResearchKeys } from '@/queries/useMarkProductQuery'
import { useBaseStore } from '@/store'
import { authStateSelector } from '@/store/authSlice'
import { decodeJwt } from 'jose'
import { useResaleQuery } from '@/queries/useResaleQuery'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
    isFixedMark?: boolean
    mark?: string | null
    holder?: string | null
}

const AddDialog = ({ isOpen, onClose, isFixedMark, mark, holder }: AddDialogProps) => {
    const queryClient = useQueryClient()
    const { toast } = useToast() 
    const { token } = useBaseStore(authStateSelector)
    const { data: resalesData } = useResaleQuery()

    const decodedToken = useMemo(() => {
        if (!token) return null
        try {
            return decodeJwt(token) as any
        } catch {
            return null
        }
    }, [token])

    const perfil = decodedToken?.usuario?.perfil
    const revendasPermitidas: number[] = useMemo(() => {
        const list = decodedToken?.usuario?.revendas_permitidas || []
        return list.map(Number)
    }, [decodedToken])

    const availableResales = useMemo(() => {
        const allResales = resalesData || []
        if (perfil === 'ADMINISTRADOR') {
            return allResales
        }
        if (perfil === 'ADMINISTRADOR_REVENDA' || revendasPermitidas.length > 0) {
            return allResales.filter((r) => revendasPermitidas.includes(r.SK_REVENDA))
        }
        return allResales
    }, [resalesData, perfil, revendasPermitidas])

    const showResaleSelect = useMemo(() => {
        if (perfil === 'ADMINISTRADOR') return true
        if (perfil === 'ADMINISTRADOR_REVENDA' && revendasPermitidas.length > 1) return true
        if (revendasPermitidas.length > 1) return true
        return false
    }, [perfil, revendasPermitidas])

    const schema = z.object({
        DS_PRODUTO: z.string().min(1, {
            message: 'O campo produto é obrigatório',
        }).max(100, 'Quantidade máxima de 100 caracteres'),
        DD_SKU: z.string().min(1, {
            message: 'O campo SKU é obrigatório',
        }).max(45, 'Quantidade máxima de 45 caracteres'),
        DD_UNIDADE_VENDA: z.string().min(1, {
            message: 'O campo unidade de venda é obrigatório',
        }),
        DD_MARCA: z.string().min(1, {
            message: 'O campo marca é obrigatório',
        }).max(50, 'Quantidade máxima de 50 caracteres'),
        DS_DETENTOR: z.string().min(1, {
            message: 'O campo fabricante é obrigatório',
        }).max(100, 'Quantidade máxima de 100 caracteres'),
        SK_REVENDA: z.string().optional().nullable(),
    });

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            DS_PRODUTO: '',
            DD_SKU: '',
            DD_UNIDADE_VENDA: '',
            DD_MARCA: mark || '',
            DS_DETENTOR: holder || '',
            SK_REVENDA: perfil === 'ADMINISTRADOR' ? 'CORPORATIVO' : (revendasPermitidas.length === 1 ? revendasPermitidas[0].toString() : ''),
        },
    })

    useEffect(() => {
        if (isOpen) {
            if (mark && holder) {
                form.setValue('DD_MARCA', mark)
                form.setValue('DS_DETENTOR', holder)
            }
            if (perfil === 'ADMINISTRADOR') {
                form.setValue('SK_REVENDA', 'CORPORATIVO')
            } else if (revendasPermitidas.length === 1) {
                form.setValue('SK_REVENDA', revendasPermitidas[0].toString())
            } else if (revendasPermitidas.length > 1 && !form.getValues('SK_REVENDA')) {
                form.setValue('SK_REVENDA', revendasPermitidas[0].toString())
            }
        }
    }, [isOpen, mark, holder, perfil, revendasPermitidas])

    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: createProduct,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ProductsKeys.lists() })
            queryClient.invalidateQueries({ queryKey: MarkProductInMarketResearchKeys.all })
            onClose()
            form.reset()
            toast({
                title: 'Produto criado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar o produto. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar produto',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {
        const skRevendaNum =
            data.SK_REVENDA && data.SK_REVENDA !== 'CORPORATIVO'
                ? Number(data.SK_REVENDA)
                : null

        mutate({
            ...data,
            DD_UNIDADE_VENDA: Number(data.DD_UNIDADE_VENDA),
            DD_PRODUTO_VALIDADO: isFixedMark ? 1 : 0,
            SK_REVENDA: skRevendaNum,
        })
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[700px] overflow-auto'>
                <DialogHeader className='px-2'>
                    <DialogTitle>Adicionar Produto</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        {showResaleSelect && (
                            <div className='grid grid-cols-[1fr] gap-4 mb-2'>
                                <FormField
                                    name="SK_REVENDA"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Revenda</FormLabel>
                                            <Select
                                                onValueChange={field.onChange}
                                                value={field.value || (perfil === 'ADMINISTRADOR' ? 'CORPORATIVO' : '')}
                                            >
                                                <FormControl>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Selecione uma revenda" />
                                                    </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                    {perfil === 'ADMINISTRADOR' && (
                                                        <SelectItem value="CORPORATIVO">
                                                            Corporativo (Central)
                                                        </SelectItem>
                                                    )}
                                                    {availableResales.map((resale) => (
                                                        <SelectItem
                                                            key={resale.SK_REVENDA}
                                                            value={resale.SK_REVENDA.toString()}
                                                        >
                                                            {resale.DS_REVENDA} ({resale.DD_UF})
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )}
                        <div className='grid grid-cols-[1fr] gap-4'>
                            <FormField
                                name="DS_PRODUTO"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Produto</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid md:grid-cols-[1fr,1fr,1fr] gap-4'>
                            <FormField
                                name="DD_SKU"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Embalagem</FormLabel>
                                        <FormControl>
                                            <Input {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />  
                            <FormField
                                name="DD_UNIDADE_VENDA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Unidade venda</FormLabel>
                                        <FormControl>
                                            <Input {...field} type='number'/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                            <FormField
                                name="DD_MARCA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Marca</FormLabel>
                                        <FormControl>
                                            <Input {...field}/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 
                        </div>
                        <div className='grid grid-cols-[1fr] md:grid-cols-[1fr,1fr]  gap-4'> 
                            <FormField
                                name="DS_DETENTOR"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Fabricante</FormLabel>
                                        <FormControl>
                                            <Input {...field}/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />  
                        </div> 
                        <DialogFooter className="mt-4">
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddDialog

